import React, { useState, useEffect } from 'react';
import MainNavbar from './MainNavbar';
import { getApiUrl, API_CONFIG } from '../config/api';

const AltaForm = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [temporada, setTemporada] = useState('');
  const [formData, setFormData] = useState({
    nombre_alumno: '',
    apellidos_alumno: '',
    dni_alumno: '',
    fecha_nacimiento: '',
    direccion: '',
    poblacion: '',
    curso: '',
    categoria_id: '',
    nombre_padre: '',
    apellidos_padre: '',
    dni_padre: '',
    telefono_padre: '',
    email_padre: ''
  });
  const [files, setFiles] = useState({
    foto_alumno: null,
    foto_padre: null,
    dni_alumno_frontal: null,
    dni_alumno_trasero: null,
    dni_padre_frontal: null
  });

  useEffect(() => {
    const fetchTemporada = async () => {
      try {
        const res = await fetch('/api/temporada-actual');
        const data = await res.json();
        setTemporada(data.temporada);
      } catch (e) {
        setTemporada('2026/27');
      }
    };
    fetchTemporada();

    const fetchCategorias = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/categorias');
        
        if (!response.ok) {
          throw new Error('Error al cargar categorías');
        }
        
        const data = await response.json();
        setCategorias(data);
        
      } catch (error) {
        console.error('Error al cargar categorías:', error);
        setMessage('Error cargando categorías');
      } finally {
        setLoading(false);
      }
    };

    fetchCategorias();
  }, []);

  const obtenerAnioNacimiento = (fecha) => {
    if (!fecha) return null;
    return parseInt(fecha.split('-')[0], 10);
  };

  const categoriasFiltradas = React.useMemo(() => {
    const anioNac = obtenerAnioNacimiento(formData.fecha_nacimiento);
    if (!anioNac) return categorias;
    return categorias.filter(c =>
      !c.anio_min || !c.anio_max ||
      (anioNac >= c.anio_min && anioNac <= c.anio_max)
    );
  }, [categorias, formData.fecha_nacimiento]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'fecha_nacimiento') {
      const nuevoAnio = obtenerAnioNacimiento(value);
      const categoriaActual = categorias.find(c => c.id === parseInt(formData.categoria_id, 10));
      if (categoriaActual && categoriaActual.anio_min && categoriaActual.anio_max &&
          (nuevoAnio < categoriaActual.anio_min || nuevoAnio > categoriaActual.anio_max)) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          categoria_id: ''
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles && selectedFiles[0]) {
      const file = selectedFiles[0];
      
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setMessage('Por favor selecciona un archivo de imagen válido');
        return;
      }
      
      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('El archivo es demasiado grande. Máximo 5MB');
        return;
      }
      
      setFiles(prev => ({
        ...prev,
        [name]: file
      }));
      
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre_alumno || !formData.apellidos_alumno || !formData.fecha_nacimiento) {
      setMessage('Por favor completa los campos obligatorios del alumno');
      return;
    }
    
    if (!formData.nombre_padre || !formData.apellidos_padre || !formData.telefono_padre) {
      setMessage('Por favor completa los campos obligatorios del tutor');
      return;
    }
    
    if (!formData.categoria_id) {
      setMessage('Por favor selecciona una categoría');
      return;
    }

    const categoriaSeleccionada = categorias.find(c => c.id === parseInt(formData.categoria_id, 10));
    const anioNac = obtenerAnioNacimiento(formData.fecha_nacimiento);
    if (categoriaSeleccionada && anioNac && categoriaSeleccionada.anio_min && categoriaSeleccionada.anio_max &&
        (anioNac < categoriaSeleccionada.anio_min || anioNac > categoriaSeleccionada.anio_max)) {
      setMessage(`La categoría seleccionada (${categoriaSeleccionada.nombre}) no corresponde a un niño nacido en ${anioNac}. Corresponde a años ${categoriaSeleccionada.anio_min}-${categoriaSeleccionada.anio_max}.`);
      return;
    }
    
    if (!files.foto_alumno || !files.foto_padre) {
      setMessage('Por favor sube las fotos del alumno y del tutor');
      return;
    }

    if (!files.dni_alumno_frontal || !files.dni_alumno_trasero) {
      setMessage('Por favor sube el DNI del niño o el Libro de familia por ambas caras');
      return;
    }

    if (!files.dni_padre_frontal) {
      setMessage('Por favor sube el DNI del padre/madre/tutor (cara frontal)');
      return;
    }
    
    try {
      setLoading(true);
      setMessage('');
      
      const formDataToSend = new FormData();
      
      // Añadir datos del formulario
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Añadir archivos
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });
      
      const response = await fetch('/api/alta', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.status === 413) {
        setMessage('Las fotos pesan demasiado. Prueba a hacerlas de nuevo con más espacio o desde otro móvil.');
        setLoading(false);
        return;
      }

      let result;
      try {
        result = await response.json();
      } catch (e) {
        setMessage(`Error del servidor (${response.status}). Inténtalo de nuevo.`);
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        setMessage('¡Registro completado exitosamente!');
        // Resetear formulario
        setFormData({
          nombre_alumno: '',
          apellidos_alumno: '',
          dni_alumno: '',
          fecha_nacimiento: '',
          direccion: '',
          poblacion: '',
          curso: '',
          categoria_id: '',
          nombre_padre: '',
          apellidos_padre: '',
          dni_padre: '',
          telefono_padre: '',
          email_padre: ''
        });
        setFiles({
          foto_alumno: null,
          foto_padre: null,
          dni_alumno_frontal: null,
          dni_alumno_trasero: null,
          dni_padre_frontal: null
        });
        // Resetear inputs de archivo
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => input.value = '');
      } else {
        setMessage(result.error || 'Error en el registro');
      }
      
    } catch (error) {
      console.error('Error en el registro:', error);
      setMessage('Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <MainNavbar />
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Registro de Alumno - Escuelas Deportivas
              </h1>
              {temporada && (
                <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full mb-2">
                  Temporada {temporada}
                </span>
              )}
              <p className="text-gray-600">Complete todos los campos para registrar al alumno</p>
            </div>

            {message && (
              <div className={`mb-6 p-4 rounded-lg ${
                (message.includes('correctamente') || message.includes('exitosamente')) 
                  ? 'bg-green-100 border border-green-400 text-green-700' 
                  : 'bg-red-100 border border-red-400 text-red-700'
              }`}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Datos del Alumno */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
                  Datos del Alumno
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      name="nombre_alumno"
                      value={formData.nombre_alumno}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellidos *
                    </label>
                    <input
                      type="text"
                      name="apellidos_alumno"
                      value={formData.apellidos_alumno}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DNI (opcional)
                    </label>
                    <input
                      type="text"
                      name="dni_alumno"
                      value={formData.dni_alumno}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      name="fecha_nacimiento"
                      value={formData.fecha_nacimiento}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dirección *
                    </label>
                    <input
                      type="text"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Población *
                    </label>
                    <input
                      type="text"
                      name="poblacion"
                      value={formData.poblacion}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Curso *
                    </label>
                    <select
                      name="curso"
                      value={formData.curso}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">Selecciona un curso</option>
                      {[
                        'Mini 4 años',
                        'Mini 5 años',
                        '1º Primaria',
                        '2º Primaria',
                        '3º Primaria',
                        '4º Primaria',
                        '5º Primaria',
                        '6º Primaria',
                        '1º ESO',
                        '2º ESO',
                        '3º ESO',
                        '4º ESO'
                      ].map((opcion) => (
                        <option key={opcion} value={opcion}>{opcion}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grupo / Horario (opción preferida) *
                  </label>
                  <select
                    name="categoria_id"
                    value={formData.categoria_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecciona una categoría</option>
                    {categoriasFiltradas.length === 0 && formData.fecha_nacimiento ? (
                      <option value="" disabled>No hay grupos para el año de nacimiento {obtenerAnioNacimiento(formData.fecha_nacimiento)}</option>
                    ) : (
                      categoriasFiltradas.map((categoria) => {
                        return (
                          <option key={categoria.id} value={categoria.id}>
                            {categoria.nombre}
                            {categoria.anio_min ? ` (${categoria.anio_min}-${categoria.anio_max})` : ''}
                            {' '}- {categoria.dias_entreno} - {categoria.hora_inicio} a {categoria.hora_fin}
                            {categoria.limite_alumnos ? ` - ${categoria.alumnos_inscritos}/${categoria.limite_alumnos} plazas` : ''}
                          </option>
                        );
                      })
                    )}
                  </select>
                </div>


                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Foto carnet del niño *
                  </label>
                  <input
                    type="file"
                    name="foto_alumno"
                    onChange={handleFileChange}
                    accept="image/*"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {files.foto_alumno && <p className="text-xs text-green-600 mt-1">✓ {files.foto_alumno.name}</p>}
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DNI del niño o Libro de familia - Cara frontal *
                    </label>
                    <input
                      type="file"
                      name="dni_alumno_frontal"
                      onChange={handleFileChange}
                      accept="image/*"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {files.dni_alumno_frontal && <p className="text-xs text-green-600 mt-1">✓ {files.dni_alumno_frontal.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DNI del niño o Libro de familia - Cara trasera (cara dos) *
                    </label>
                    <input
                      type="file"
                      name="dni_alumno_trasero"
                      onChange={handleFileChange}
                      accept="image/*"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {files.dni_alumno_trasero && <p className="text-xs text-green-600 mt-1">✓ {files.dni_alumno_trasero.name}</p>}
                  </div>
                </div>
              </div>

              {/* Datos del Tutor */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
                  Datos del Tutor/Padre
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre del Padre/Tutor *
                    </label>
                    <input
                      type="text"
                      name="nombre_padre"
                      value={formData.nombre_padre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellidos del Padre/Tutor *
                    </label>
                    <input
                      type="text"
                      name="apellidos_padre"
                      value={formData.apellidos_padre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      name="telefono_padre"
                      value={formData.telefono_padre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email_padre"
                      value={formData.email_padre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DNI del Padre/Tutor *
                    </label>
                    <input
                      type="text"
                      name="dni_padre"
                      value={formData.dni_padre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Foto del Tutor/Padre *
                  </label>
                  <input
                    type="file"
                    name="foto_padre"
                    onChange={handleFileChange}
                    accept="image/*"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {files.foto_padre && <p className="text-xs text-green-600 mt-1">✓ {files.foto_padre.name}</p>}
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DNI del tutor - Cara frontal *
                  </label>
                  <input
                    type="file"
                    name="dni_padre_frontal"
                    onChange={handleFileChange}
                    accept="image/*"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {files.dni_padre_frontal && <p className="text-xs text-green-600 mt-1">✓ {files.dni_padre_frontal.name}</p>}
                </div>
              </div>

              {/* Botón de envío */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-3 px-8 rounded-lg text-lg transition duration-200"
                >
                  {loading ? 'Registrando...' : 'Registrar Alumno'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AltaForm;
